// ============================================================
// Pay Rocky - User Dashboard
// ============================================================

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ArrowDownCircle,
  ArrowUpCircle,
  Clock,
  LogOut,
  User,
  ShieldCheck,
  ChevronRight,
  Eye,
  EyeOff,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../../context/AuthContext';
import { useOrders } from '../../context/OrdersContext';
import { Badge, Card, MetricCard, Button, Divider, EmptyState, FormField, TextInput, LoadingCard, Skeleton } from '../../components/ui/Primitives';
import TelegramCommunityCard from '../../components/ui/TelegramCommunityCard';
import { SUPPORT_EMAIL } from '../../data/constants';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import { formatNGN, formatDate, formatDateShort, getInitials, capitalize, maskDocumentNumber } from '../../utils/helpers';

const NAV = [
  { id: 'home', label: 'Home', icon: LayoutDashboard, to: '/' },
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'transactions', label: 'Transactions', icon: Clock },
  { id: 'profile', label: 'Profile', icon: User },
];
const USER_DASHBOARD_REQUEST_TIMEOUT_MS = 10000;

export default function UserDashboard() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(() => (currentUser?.must_change_password ? 'profile' : 'overview'));
  const supportEmail = useSupportEmail(SUPPORT_EMAIL, USER_DASHBOARD_REQUEST_TIMEOUT_MS);
  const isPasswordChangeRequired = Boolean(currentUser?.must_change_password);
  const isBuyRestricted = isPasswordChangeRequired || ['unverified', 'rejected', 'suspended'].includes(currentUser?.kyc_status);
  const activeLabel = NAV.find((item) => item.id === active)?.label || 'Overview';

  useEffect(() => {
    if (currentUser?.must_change_password) {
      setActive('profile');
    }
  }, [currentUser?.must_change_password]);

  useEffect(() => {
    if (location.state?.forceProfile) {
      setActive('profile');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  return (
    <div className="dashboard-shell dashboard-shell-user">
      <aside className="dashboard-sidebar user-dashboard-sidebar">
        <Link to="/" className="dashboard-sidebar-brand">
          <div className="dashboard-sidebar-brand-mark dashboard-sidebar-brand-mark-user">P</div>
          <div>
            <div className="dashboard-sidebar-brand-text">Pay<span style={{ color: 'var(--clr-accent)' }}>Rocky</span></div>
            <div className="dashboard-sidebar-brand-sub">Personal Trading Space</div>
          </div>
        </Link>

        <div className="dashboard-sidebar-profile">
          <div className="dashboard-sidebar-avatar dashboard-sidebar-avatar-user">{getInitials(currentUser?.name)}</div>
          <div style={{ minWidth: 0 }}>
            <div className="dashboard-sidebar-profile-name">{currentUser?.name}</div>
            <div className="dashboard-sidebar-profile-meta">{currentUser?.kyc_status === 'verified' ? 'Verified profile' : capitalize(currentUser?.kyc_status || 'unverified')}</div>
          </div>
        </div>

        <Divider style={{ margin: '0 0 1rem' }} />

        <div className="dashboard-sidebar-section-label">Navigation</div>

        <nav className="dashboard-nav" style={{ flex: 1 }}>
          {NAV.map(({ id, label, icon: Icon, to }) => (
            <button
              key={id}
              onClick={() => (to ? navigate(to) : setActive(id))}
              className={`dashboard-nav-button ${active === id && !to ? 'is-active is-user' : ''}`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </nav>

        <Card className="dashboard-sidebar-card dashboard-sidebar-card-user">
          <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Current view</div>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '6px' }}>{activeLabel}</div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-2)', lineHeight: 1.6 }}>
            Keep your profile, transactions, and trading actions within one calm, readable workspace.
          </div>
        </Card>

        <div style={{ paddingTop: '0.9rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button variant="primary" size="sm" fullWidth disabled={isBuyRestricted} onClick={() => navigate('/buy')}>
            <ArrowDownCircle size={13} /> Buy Crypto
          </Button>
          <Button variant="ghost" size="sm" fullWidth disabled={isPasswordChangeRequired} onClick={() => navigate('/sell')}>
            <ArrowUpCircle size={13} /> Sell Crypto
          </Button>
          <button onClick={() => { logout(); navigate('/'); }} className="dashboard-logout-button">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      <main className="dashboard-main user-dashboard-main">
        <div className="dashboard-topbar">
          <div>
            <div className="dashboard-eyebrow">User Dashboard</div>
            <h1 className="dashboard-page-title">{activeLabel}</h1>
          </div>
        <div className="dashboard-pill-row">
          <span className="dashboard-pill dashboard-pill-user">{currentUser?.kyc_status === 'verified' ? 'Verified account' : `${capitalize(currentUser?.kyc_status || 'unverified')} account`}</span>
          <span className="dashboard-pill">{isPasswordChangeRequired ? 'Password action required' : 'Account ready'}</span>
        </div>
      </div>

        {currentUser?.must_change_password ? (
          <Card style={{ marginBottom: '1rem', border: '1px solid rgba(255, 171, 0, 0.35)', background: 'rgba(255, 171, 0, 0.08)' }}>
            <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--clr-text)', marginBottom: '6px' }}>Temporary password in use</div>
            <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
              PayRocky requires you to change the temporary password on your profile before you can continue with new orders or account updates.
            </div>
          </Card>
        ) : null}
        <div className="user-dashboard-mobile-shell dashboard-mobile-shell">
          <Card style={{ padding: '1rem', marginBottom: '0.9rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <Link to="/" className="dashboard-sidebar-brand" style={{ marginBottom: 0 }}>
                <div className="dashboard-sidebar-brand-mark dashboard-sidebar-brand-mark-user" style={{ width: '28px', height: '28px' }}>P</div>
                <span className="dashboard-sidebar-brand-text" style={{ color: 'var(--clr-text)' }}>Pay<span style={{ color: 'var(--clr-accent)' }}>Rocky</span></span>
              </Link>
              <span className="dashboard-pill dashboard-pill-user">{capitalize(currentUser?.kyc_status || 'unverified')}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'var(--clr-accent-dim)', border: '1px solid var(--clr-accent-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '12px', color: 'var(--clr-accent)', flexShrink: 0 }}>
                {getInitials(currentUser?.name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--clr-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--clr-text-3)' }}>{currentUser?.email}</div>
              </div>
            </div>

            <div className="user-dashboard-mobile-nav dashboard-mobile-nav-grid">
              {NAV.map(({ id, label, icon: Icon, to }) => (
                <button
                  key={id}
                  onClick={() => (to ? navigate(to) : setActive(id))}
                  className={`dashboard-mobile-nav-button ${active === id && !to ? 'is-active is-user' : ''}`}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Button variant="primary" size="sm" fullWidth disabled={isBuyRestricted} onClick={() => navigate('/buy')}>
                <ArrowDownCircle size={13} /> Buy
              </Button>
              <Button variant="ghost" size="sm" fullWidth disabled={isPasswordChangeRequired} onClick={() => navigate('/sell')}>
                <ArrowUpCircle size={13} /> Sell
              </Button>
            </div>

            <button onClick={() => { logout(); navigate('/'); }} className="dashboard-logout-button" style={{ width: '100%', justifyContent: 'center' }}>
              <LogOut size={13} /> Sign out
            </button>
          </Card>
        </div>

        {active === 'overview' && <Overview supportEmail={supportEmail} />}
        {active === 'transactions' && <Transactions />}
        {active === 'profile' && <Profile />}
      </main>

      <style>{`
        @media (max-width: 900px) {
          .user-dashboard-sidebar {
            display: none !important;
          }

          .user-dashboard-mobile-shell {
            display: block !important;
          }
        }

        @media (max-width: 560px) {
          .user-dashboard-mobile-nav {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function Overview({ supportEmail }) {
  const { currentUser } = useAuth();
  const { getUserOrders, loading } = useOrders();
  const navigate = useNavigate();
  const isPasswordChangeRequired = Boolean(currentUser?.must_change_password);
  const isBuyRestricted = isPasswordChangeRequired || ['unverified', 'rejected', 'suspended'].includes(currentUser?.kyc_status);
  const isSuspended = currentUser?.kyc_status === 'suspended';
  const orders = getUserOrders(currentUser?.id);
  const stats = {
    total: orders.length,
    completed: orders.filter((order) => order.status === 'COMPLETED').length,
    pending: orders.filter((order) => order.status === 'PENDING' || order.status === 'PROCESSING').length,
    cancelled: orders.filter((order) => order.status === 'CANCELLED').length,
    totalValue: orders
      .filter((order) => order.status === 'COMPLETED')
      .reduce((sum, order) => sum + Number(order.naira_value || 0), 0),
  };

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 800, marginBottom: '2px' }}>Good day, {currentUser?.name?.split(' ')[0]}</h1>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-3)' }}>Here is your trading overview</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button variant="primary" size="sm" disabled={isBuyRestricted} onClick={() => navigate('/buy')}>Buy Crypto</Button>
          <Button variant="ghost" size="sm" disabled={isPasswordChangeRequired} onClick={() => navigate('/sell')}>Sell Crypto</Button>
        </div>
      </div>

      <div className="user-metric-grid dashboard-metric-grid dashboard-metric-grid-user" style={{ marginBottom: '1rem' }}>
        {loading && orders.length === 0 ? (
          Array.from({ length: 5 }).map((_, index) => (
            <Card key={`user-metric-skeleton-${index}`} style={{ padding: '1rem' }}>
              <Skeleton width="42%" height="11px" style={{ marginBottom: '0.9rem' }} />
              <Skeleton width={index === 4 ? '70%' : '52%'} height="26px" radius="12px" />
            </Card>
          ))
        ) : (
          <>
            <MetricCard label="Total orders" value={stats.total} />
            <MetricCard label="Completed" value={stats.completed} accent />
            <MetricCard label="Pending" value={stats.pending} />
            <MetricCard label="Cancelled" value={stats.cancelled} />
            <MetricCard label="Transaction Volume" value={stats.totalValue > 0 ? formatNGN(stats.totalValue) : 'N0'} />
          </>
        )}
      </div>

      {isSuspended && (
        <Card style={{ marginBottom: '1rem', background: 'linear-gradient(135deg, rgba(176, 58, 48, 0.08) 0%, rgba(255,255,255,0.96) 100%)' }}>
          <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '6px' }}>
            Buy access is suspended on your profile
          </div>
          <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
            You can still place sell orders. If you want to restore full access, contact PayRocky support at{' '}
            <a href={`mailto:${supportEmail}`} style={{ color: 'var(--clr-accent)', fontWeight: 700 }}>
              {supportEmail}
            </a>.
          </div>
        </Card>
      )}

      {currentUser?.kyc_status !== 'verified' && <KycVerificationPanel compact />}

      <div className="dashboard-section-heading">Recent transactions</div>

      <RecentTransactions />

      <div style={{ marginTop: '1.5rem' }}>
        <TelegramCommunityCard compact title="Join our fast-growing Telegram learning community" description="Stay close to the PayRocky community for educational content, market conversations, and interactive updates that help you trade with more confidence." />
      </div>

      <style>{`
        @media (max-width: 900px) {
          .user-metric-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }

        @media (max-width: 560px) {
          .user-metric-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function RecentTransactions() {
  const { currentUser } = useAuth();
  const { getUserOrders, loading } = useOrders();
  const navigate = useNavigate();
  const recent = getUserOrders(currentUser?.id).slice(0, 5);
  const isBuyRestricted = ['unverified', 'rejected', 'suspended'].includes(currentUser?.kyc_status);

  return (
    <>
      <Card className="user-transactions-desktop" style={{ padding: 0, overflow: 'hidden' }}>
        {loading && recent.length === 0 ? (
          <div style={{ padding: '1rem' }}>
            <LoadingCard lines={4} titleWidth="28%" />
          </div>
        ) : recent.length === 0 ? (
          <EmptyState icon="Orders" title="No orders yet" description="Start by placing a buy or sell order." action={<Button variant="primary" disabled={isBuyRestricted} onClick={() => navigate('/buy')}>Place first order</Button>} />
        ) : (
          <>
            <TransactionHeader />
            {recent.map((order) => <TransactionRow key={order.id} order={order} />)}
          </>
        )}
      </Card>

      <div className="user-transactions-mobile" style={{ display: 'none', gap: '10px' }}>
        {loading && recent.length === 0 ? (
          <Card>
            <LoadingCard lines={3} titleWidth="32%" />
          </Card>
        ) : recent.length === 0 ? (
          <Card>
            <EmptyState icon="Orders" title="No orders yet" description="Start by placing a buy or sell order." action={<Button variant="primary" disabled={isBuyRestricted} onClick={() => navigate('/buy')}>Place first order</Button>} />
          </Card>
        ) : (
          recent.map((order) => <TransactionCard key={order.id} order={order} />)
        )}
      </div>

      <style>{`
        @media (max-width: 860px) {
          .user-transactions-desktop {
            display: none !important;
          }

          .user-transactions-mobile {
            display: grid !important;
          }
        }
      `}</style>
    </>
  );
}

function Transactions() {
  const { currentUser } = useAuth();
  const { getUserOrders, loading } = useOrders();
  const [filter, setFilter] = useState('ALL');
  const allOrders = getUserOrders(currentUser?.id);
  const filters = ['ALL', 'PENDING', 'PROCESSING', 'COMPLETED', 'CANCELLED'];
  const filtered = filter === 'ALL' ? allOrders : allOrders.filter((order) => order.status === filter);

  return (
    <div className="fade-up">
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '1.5rem' }}>Transactions</h2>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {filters.map((currentFilter) => (
          <button key={currentFilter} onClick={() => setFilter(currentFilter)} style={{ padding: '5px 14px', borderRadius: '99px', fontSize: '12px', fontWeight: 500, border: '1px solid', background: filter === currentFilter ? 'var(--clr-accent)' : 'transparent', borderColor: filter === currentFilter ? 'var(--clr-accent)' : 'var(--clr-border)', color: filter === currentFilter ? 'var(--clr-on-accent)' : 'var(--clr-text-2)', transition: 'all 0.15s' }}>
            {capitalize(currentFilter)}
          </button>
        ))}
      </div>

      <Card className="user-transactions-desktop" style={{ padding: 0, overflow: 'hidden' }}>
        {loading && allOrders.length === 0 ? (
          <div style={{ padding: '1rem' }}>
            <LoadingCard lines={5} titleWidth="24%" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="None" title={`No ${filter.toLowerCase()} orders`} description="Try a different filter." />
        ) : (
          <>
            <TransactionHeader />
            {filtered.map((order) => <TransactionRow key={order.id} order={order} />)}
          </>
        )}
      </Card>

      <div className="user-transactions-mobile" style={{ display: 'none', gap: '10px' }}>
        {loading && allOrders.length === 0 ? (
          <Card><LoadingCard lines={3} titleWidth="30%" /></Card>
        ) : filtered.length === 0 ? (
          <Card><EmptyState icon="None" title={`No ${filter.toLowerCase()} orders`} description="Try a different filter." /></Card>
        ) : (
          filtered.map((order) => <TransactionCard key={order.id} order={order} />)
        )}
      </div>

      <style>{`
        @media (max-width: 860px) {
          .user-transactions-desktop {
            display: none !important;
          }

          .user-transactions-mobile {
            display: grid !important;
          }
        }
      `}</style>
    </div>
  );
}

function Profile() {
  const { currentUser, changePassword } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const setPasswordField = (key) => (event) => {
    setPasswordForm((prev) => ({ ...prev, [key]: event.target.value }));
    setPasswordErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};

    if (!passwordForm.currentPassword) nextErrors.currentPassword = 'Enter your current password';
    if (!passwordForm.newPassword) nextErrors.newPassword = 'Enter your new password';
    if (passwordForm.newPassword !== passwordForm.confirmPassword) nextErrors.confirmPassword = 'Passwords do not match';

    setPasswordErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setPasswordLoading(true);
    const response = await changePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
    setPasswordLoading(false);

    if (response.success) {
      toast.success(response.message || 'Password changed successfully.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordErrors({});
    } else {
      setPasswordErrors((prev) => ({ ...prev, newPassword: response.error || 'Password update failed' }));
      toast.error(response.error || 'Password update failed');
    }
  };

  return (
    <div className="fade-up" style={{ maxWidth: '760px' }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 800, marginBottom: '1.5rem' }}>Profile Settings</h2>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '1rem' }}>Account Info</div>
        <div className="user-profile-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <InfoBlock label="Full name" value={currentUser?.name} />
          <InfoBlock label="Email" value={currentUser?.email} />
          <InfoBlock label="Phone" value={currentUser?.phone || '-'} />
          <div>
            <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '4px' }}>KYC Status</div>
            <Badge status={currentUser?.kyc_status}>{capitalize(currentUser?.kyc_status || 'unverified')}</Badge>
          </div>
        </div>
      </Card>

      <KycVerificationPanel />

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '10px' }}>Change password</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7, marginBottom: '1rem' }}>
          {currentUser?.must_change_password
            ? 'Your account is currently using a temporary password. Change it here now to unlock new orders and profile actions.'
            : 'Update your password here anytime. If PayRocky gives you a temporary password, sign in with it first, then change it immediately from this section.'}
        </div>

        <form onSubmit={handlePasswordSubmit}>
          <FormField label="Current password" error={passwordErrors.currentPassword}>
            <div className="password-field-shell" style={{ position: 'relative' }}>
              <TextInput className="password-input-with-toggle" type={showPasswords.current ? 'text' : 'password'} value={passwordForm.currentPassword} onChange={setPasswordField('currentPassword')} error={passwordErrors.currentPassword} style={{ paddingRight: '52px' }} />
              <button type="button" className="auth-password-toggle" onClick={() => setShowPasswords((prev) => ({ ...prev, current: !prev.current }))} aria-label={showPasswords.current ? 'Hide current password' : 'Show current password'}>
                {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </FormField>
          <FormField label="New password" error={passwordErrors.newPassword}>
            <div className="password-field-shell" style={{ position: 'relative' }}>
              <TextInput className="password-input-with-toggle" type={showPasswords.next ? 'text' : 'password'} value={passwordForm.newPassword} onChange={setPasswordField('newPassword')} error={passwordErrors.newPassword} style={{ paddingRight: '52px' }} />
              <button type="button" className="auth-password-toggle" onClick={() => setShowPasswords((prev) => ({ ...prev, next: !prev.next }))} aria-label={showPasswords.next ? 'Hide new password' : 'Show new password'}>
                {showPasswords.next ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </FormField>
          <FormField label="Confirm new password" error={passwordErrors.confirmPassword}>
            <div className="password-field-shell" style={{ position: 'relative' }}>
              <TextInput className="password-input-with-toggle" type={showPasswords.confirm ? 'text' : 'password'} value={passwordForm.confirmPassword} onChange={setPasswordField('confirmPassword')} error={passwordErrors.confirmPassword} style={{ paddingRight: '52px' }} />
              <button type="button" className="auth-password-toggle" onClick={() => setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))} aria-label={showPasswords.confirm ? 'Hide confirm password' : 'Show confirm password'}>
                {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </FormField>

          <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '1rem', lineHeight: 1.6 }}>
            Your new password must include at least 8 characters, an uppercase letter, a lowercase letter, a number, and a special character.
          </div>

          <Button type="submit" variant="primary" loading={passwordLoading}>
            Update password
          </Button>
        </form>
      </Card>

      <Card>
        <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '10px' }}>Verification note</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
          Enter your 11-digit NIN to verify your identity. If your NIN is validated successfully, your account can be verified automatically. PayRocky may still place an account on rejection or suspension if further review is needed.
        </div>
      </Card>

      <style>{`
        @media (max-width: 640px) {
          .user-profile-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function KycVerificationPanel({ compact = false }) {
  const { currentUser, submitKyc } = useAuth();
  const isVerified = currentUser?.kyc_status === 'verified';
  const isPending = currentUser?.kyc_status === 'pending';
  const canSubmitKyc = currentUser?.kyc_status === 'unverified' || currentUser?.kyc_status === 'rejected';
  const [form, setForm] = useState({
    documentType: 'NIN',
    documentNumber: currentUser?.kyc_document_number || '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const normalizedNin = String(form.documentNumber || '').replace(/\D/g, '');
    if (!normalizedNin) nextErrors.documentNumber = 'Enter your 11-digit NIN';
    if (normalizedNin && normalizedNin.length !== 11) nextErrors.documentNumber = 'NIN must be exactly 11 digits';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    if (!canSubmitKyc) {
      toast.error('Your KYC submission is already under review.');
      return;
    }

    setLoading(true);
    const response = await submitKyc({
      documentType: 'NIN',
      documentNumber: normalizedNin,
    });
    setLoading(false);

    if (response.success) {
      toast.success(response.user?.kyc_status === 'verified' ? 'NIN verified successfully' : 'NIN submitted for review');
      setErrors({});
    } else {
      toast.error(response.error || 'KYC submission failed');
    }
  };

  return (
    <Card style={{ marginBottom: compact ? '1.5rem' : '1rem', background: compact ? 'linear-gradient(135deg, rgba(143, 59, 46, 0.08) 0%, rgba(255,255,255,0.96) 100%)' : 'var(--clr-surface)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '4px' }}>Identity verification</div>
            <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>
              {isVerified
                ? 'Your profile has already been verified successfully.'
                : isPending
                  ? 'Your submitted document is under review. You can submit again only if PayRocky rejects it.'
                  : compact
                    ? 'Enter your 11-digit NIN here for automatic verification.'
                    : 'Enter your 11-digit NIN here and PayRocky will verify it automatically.'}
            </div>
          </div>
          <Badge status={currentUser?.kyc_status}>{capitalize(currentUser?.kyc_status || 'unverified')}</Badge>
        </div>

        {isVerified ? (
          <div style={{ background: 'linear-gradient(135deg, rgba(143, 59, 46, 0.08) 0%, rgba(255,255,255,0.96) 100%)', border: '1px solid rgba(143, 59, 46, 0.12)', borderRadius: 'var(--radius-md)', padding: '16px 18px' }}>
            <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '6px' }}>Profile verified</div>
            <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
              Your identity verification has been approved successfully. No further verification step is required for your account at this time.
            </div>
          </div>
        ) : isPending ? (
          <>
            {(currentUser?.kyc_document_type || currentUser?.kyc_document_file_name) && (
              <div className="user-profile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '1rem' }}>
                <InfoBlock label="Document type" value={currentUser?.kyc_document_type || '-'} />
                <InfoBlock label="Document number" value={maskDocumentNumber(currentUser?.kyc_document_number)} />
                <InfoBlock label="Submitted" value={currentUser?.kyc_submitted_at ? formatDate(currentUser?.kyc_submitted_at) : '-'} />
              </div>
            )}

            <div style={{ background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', borderRadius: 'var(--radius-md)', padding: '16px 18px', fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
              Your verification is under review. The submission form will become available again only if PayRocky rejects this request.
            </div>
          </>
        ) : (
          <>
        {(currentUser?.kyc_document_type || currentUser?.kyc_document_file_name) && (
          <div className="user-profile-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '1rem' }}>
            <InfoBlock label="Document type" value={currentUser?.kyc_document_type || '-'} />
            <InfoBlock label="Document number" value={maskDocumentNumber(currentUser?.kyc_document_number)} />
            <InfoBlock label="Submitted" value={currentUser?.kyc_submitted_at ? formatDate(currentUser?.kyc_submitted_at) : '-'} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <FormField label="NIN number" error={errors.documentNumber} hint="PayRocky currently verifies only NIN automatically.">
            <TextInput value={form.documentNumber} onChange={(event) => setForm((prev) => ({ ...prev, documentNumber: event.target.value.replace(/\D/g, '').slice(0, 11) }))} placeholder="Enter your 11-digit NIN" error={errors.documentNumber} disabled={!canSubmitKyc} inputMode="numeric" />
          </FormField>
          <div style={{ padding: '12px 14px', borderRadius: '14px', background: 'var(--clr-surface-2)', border: '1px solid var(--clr-border)', fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7, marginBottom: '1rem' }}>
            PayRocky will send your NIN securely for verification. If the NIN is valid and found, your profile can be verified automatically.
          </div>

          {canSubmitKyc ? (
            <Button type="submit" variant="primary" loading={loading}>{currentUser?.kyc_status === 'rejected' ? 'Resubmit KYC' : 'Submit KYC'}</Button>
          ) : (
            <div style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.7 }}>
              {currentUser?.kyc_status === 'pending' ? 'Your verification is under review. You can submit again only if PayRocky rejects it.' : 'Your profile is already verified. No further submission is required.'}
            </div>
          )}
        </form>
          </>
        )}
    </Card>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '11px', color: 'var(--clr-text-3)', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: 'var(--clr-text)' }}>{value}</div>
    </div>
  );
}

function TransactionHeader() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '130px 60px 90px 120px 110px 80px 80px', padding: '10px 16px', background: 'var(--clr-surface-2)', borderBottom: '1px solid var(--clr-border)', fontSize: '11px', fontWeight: 600, color: 'var(--clr-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      <span>Order ID</span><span>Type</span><span>Asset</span><span>Value</span><span>Status</span><span>Date</span><span>Open</span>
    </div>
  );
}

function TransactionRow({ order }) {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '130px 60px 90px 120px 110px 80px 80px', padding: '12px 16px', borderBottom: '1px solid var(--clr-border)', alignItems: 'center', fontSize: '13px', transition: 'background 0.12s' }} onMouseEnter={(event) => { event.currentTarget.style.background = 'var(--clr-surface-2)'; }} onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent'; }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--clr-accent)' }}>{order.order_id}</span>
      <span><Badge status={order.type}>{order.type.toUpperCase()}</Badge></span>
      <span style={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '12px' }}>{order.crypto_type}</span>
      <span style={{ fontWeight: 600 }}>{formatNGN(order.naira_value)}</span>
      <span><Badge status={order.status}>{order.status}</Badge></span>
      <span style={{ color: 'var(--clr-text-3)', fontSize: '12px' }}>{formatDateShort(order.created_at)}</span>
      <button onClick={() => navigate(`/dashboard/orders/${order.order_id}`)} style={{ color: 'var(--clr-accent)', fontSize: '12px', fontWeight: 700 }}>View</button>
    </div>
  );
}

function TransactionCard({ order }) {
  const navigate = useNavigate();
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--clr-text-3)', marginBottom: '4px' }}>{order.order_id}</div>
          <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--clr-text)' }}>{order.crypto_type.toUpperCase()}</div>
        </div>
        <Badge status={order.status}>{order.status}</Badge>
      </div>
      <div style={{ display: 'grid', gap: '6px', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Type: {order.type.toUpperCase()}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Value: {formatNGN(order.naira_value)}</div>
        <div style={{ fontSize: '13px', color: 'var(--clr-text-2)' }}>Date: {formatDateShort(order.created_at)}</div>
      </div>
      <Button variant="ghost" fullWidth onClick={() => navigate(`/dashboard/orders/${order.order_id}`)}>
        View Details <ChevronRight size={15} />
      </Button>
    </Card>
  );
}
